import type { MedChronTemplate } from "@/components/MedicalChronology/Visits/types";
import type { ChronologyVersion } from "@/components/MedicalChronology/types";
import type { MedVisit } from "@/types/medchron";
import type { MedSummaryData } from "@/types/summary";

export enum VisitDiffStatus {
  Added = "added",
  Removed = "removed",
  Modified = "modified",
  Unchanged = "unchanged",
}

export enum VisitMatchMode {
  Id = "id",
  Content = "content",
}

export class CompareVersionsConstants {
  /** Shared visit_ids, as a fraction of the smaller version, at or above which visits pair by ID. */
  static readonly ID_MATCH_MIN_OVERLAP_RATIO = 0.5;
  /** Detail-text similarity a content pair needs when its key group has unequal counts per side. */
  static readonly CONTENT_MATCH_MIN_SIMILARITY = 0.5;
}

export const COMPARE_VERSIONS_SECTIONS = {
  SUMMARY: "summary",
  VISITS: "visits",
} as const;

export type CompareVersionsSection =
  (typeof COMPARE_VERSIONS_SECTIONS)[keyof typeof COMPARE_VERSIONS_SECTIONS];

export interface TextDiffSegment {
  value: string;
  added?: boolean;
  removed?: boolean;
}

export interface FieldDiff {
  field: string;
  oldValue: unknown;
  newValue: unknown;
  changed: boolean;
}

export interface DetailCategoryDiff {
  category: string;
  /** Old category label, set only when the matched old/new pair had different raw labels (a rename). */
  previousCategory: string | null;
  oldText: string | null;
  newText: string | null;
  changed: boolean;
  segments: TextDiffSegment[];
}

export interface VisitDiffEntry {
  visitId: string;
  status: VisitDiffStatus;
  oldVisit: MedVisit | null;
  newVisit: MedVisit | null;
  fieldDiffs: FieldDiff[];
  /** Any difference in order, file, or pages of the visit's citation list. */
  citationsChanged: boolean;
  detailDiffs: DetailCategoryDiff[];
}

export interface SummaryFieldDiff {
  field: keyof MedSummaryData;
  oldValue: string | null;
  newValue: string | null;
  changed: boolean;
  segments: TextDiffSegment[];
}

export interface MatchVisitsResult {
  removed: MedVisit[];
  added: MedVisit[];
  matched: Array<[MedVisit, MedVisit]>;
}

export interface MatchVisitsWithModeResult extends MatchVisitsResult {
  matchMode: VisitMatchMode;
}

export interface ChronologyDiffResult {
  visitDiffs: VisitDiffEntry[];
  summaryDiffs: SummaryFieldDiff[];
  matchMode: VisitMatchMode;
}

export interface VersionsCompareViewProps {
  diff: ChronologyDiffResult | null;
  isLoading: boolean;
  error: string | null;
  template: MedChronTemplate | null;
  fileMapping: Record<string, string>;
  baseVersion: ChronologyVersion | null;
  compareVersion: ChronologyVersion | null;
  onClose: () => void;
  section: CompareVersionsSection;
}
